import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import PermissionRoute from '@/components/common/PermissionRoute';
import AppLayout from '@/components/common/AppLayout';
import { ADMIN_ACCESS } from '@/constants/adminAccess';

const lazyPage = (importer: () => Promise<{ default: React.ComponentType }>) =>
  async () => {
    const { default: Component } = await importer();
    return { Component };
  };

function PageLoader() {
  const theme = useTheme();
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: `3px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          borderTopColor: theme.palette.primary.main,
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/login', lazy: lazyPage(() => import('@/pages/Login')) },
  { path: '/account-recovery', lazy: lazyPage(() => import('@/pages/AccountRecovery')) },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <Suspense fallback={<PageLoader />}><AppLayout /></Suspense>,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', lazy: lazyPage(() => import('@/pages/Dashboard')) },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.userMgt} />,
            children: [
              { path: 'patient-management', lazy: lazyPage(() => import('@/pages/PatientManagement')) },
              { path: 'patient-management/:personKey', lazy: lazyPage(() => import('@/pages/UserDetail')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.audit} />,
            children: [
              { path: 'audit-search', lazy: lazyPage(() => import('@/pages/AuditSearch')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.cacheInfo} />,
            children: [
              { path: 'cache-management', lazy: lazyPage(() => import('@/pages/CacheManagement')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.sysMsgs} />,
            children: [
              { path: 'system-messages', lazy: lazyPage(() => import('@/pages/SystemMessages')) },
              { path: 'system-messages/:id', lazy: lazyPage(() => import('@/pages/SystemMessageDetail')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.userMsgs} />,
            children: [
              { path: 'user-messages', lazy: lazyPage(() => import('@/pages/UserMessages')) },
              { path: 'user-messages/new', lazy: lazyPage(() => import('@/pages/UserMessageNew')) },
              { path: 'user-messages/:guid', lazy: lazyPage(() => import('@/pages/UserMessageDetail')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.configAccess} />,
            children: [
              { path: 'portal-config', lazy: lazyPage(() => import('@/pages/PortalAppConfig')) },
              { path: 'theme-editor', lazy: lazyPage(() => import('@/pages/ThemeEditor')) },
              { path: 'theme-base-editor', lazy: lazyPage(() => import('@/pages/ThemeBaseEditor')) },
              { path: 'paragraph-config', lazy: lazyPage(() => import('@/pages/ParagraphConfig')) },
              { path: 'system-config', lazy: lazyPage(() => import('@/pages/SystemConfigEditor')) },
              { path: 'site-group-editor', lazy: lazyPage(() => import('@/pages/SiteGroupEditor')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.trainingMaterial} />,
            children: [
              { path: 'training-materials', lazy: lazyPage(() => import('@/pages/TrainingMaterials')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.configAccess} />,
            children: [
              { path: 'mobile-clinic-day-url', lazy: lazyPage(() => import('@/pages/MobileClinicDayUrl')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.systemHealth} />,
            children: [
              { path: 'system-health', lazy: lazyPage(() => import('@/pages/SystemHealth')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.errorLogs} />,
            children: [
              { path: 'error-logs', lazy: lazyPage(() => import('@/pages/ErrorLogViewer')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.workflowFailures} />,
            children: [
              { path: 'workflow-failures', lazy: lazyPage(() => import('@/pages/WorkflowFailures')) },
            ],
          },
          {
            element: <PermissionRoute accessString={ADMIN_ACCESS.lookupHistory} />,
            children: [
              { path: 'lookup-change-history', lazy: lazyPage(() => import('@/pages/LookupChangeHistory')) },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);
